import { action } from '@reatom/framework'
import { useAction, useAtom } from '@reatom/npm-react'
import { useRef } from 'react'
import styled from 'styled-components'

import { basicModeAtom } from '@root/entities/basic'
import { Network } from '@root/entities/network'
import {
  TradingAuditList,
  TradingAuditListSkeleton,
} from '@root/entities/pair/ui/common'
import { BasicPanel, openBasicDepositDialogAction } from '@root/features/basic'
import { PremiumCurrencyBanner } from '@root/features/currency/ui/currency-banner'
import { ActivePositionDetails } from '@root/features/trade/position/active-position/ui'
import { openTradeSettingsDialogEvent } from '@root/features/trade/trade-settings'
import {
  tradingAuditVisibilityAtom,
  hideTradingAuditAction,
} from '@root/features/trade/trading'
import { PairStatsPanel } from '@root/features/trade/trading/ui/pair-stats-panel'
import { TradingLiquidityAlert } from '@root/features/trade/trading-controls/ui/common/trading-liquidity-alert/trading-liquidity-alert'
import { TradingControls } from '@root/features/trade/trading-controls/ui/desktop'
import { TradingPairMetricsTable } from '@root/features/trending-pairs'
import { PREMIUM_ENABLED } from '@root/shared'
import { TauriEnvironment } from '@root/shared/layout/tauri-environment/tauri-environment'
import { FadeOverflow, hiddenScrollbarCss } from '@root/shared/ui-kit'
import {
  layoutHeightCss,
  TOPBAR_HEIGHT,
} from '@root/shared/ui-kit/utils/page-layout'

import { tradeMakerFilterAtom } from '../../atoms/reatom-trades-table'

import { TradeAuditDrawer } from './trading-audit-drawer'

import type { AsyncStateN } from '@blade/std-lib'
import type { PairModel } from '@root/entities/pair'
import type {
  BuyControlsModel,
  SellControlsModel,
} from '@root/features/trade/trading-controls/atoms/common'
import type { ClassNameProps } from '@root/shared/react'

/** Desktop `Viewport` status strip; `MainScroll` cqh already sits above it */

type TradeAreaProps = {
  pairState: AsyncStateN.AsyncStateN<unknown, PairModel>
  buyControlsModel: BuyControlsModel
  sellControlsModel: SellControlsModel
} & ClassNameProps

const openTradeSettingsAction = action((ctx) => {
  openTradeSettingsDialogEvent(ctx, {
    mode: 'drawer',
  })
}, 'openTradeSettingsAction')

export const TradeArea: React.FC<TradeAreaProps> = ({
  className,
  pairState,
  buyControlsModel,
  sellControlsModel,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const openTradeSettings = useAction(openTradeSettingsAction)
  const [{ open: auditOpen }] = useAtom(tradingAuditVisibilityAtom)
  const hideAudit = useAction(hideTradingAuditAction)
  const [makerFilter] = useAtom(tradeMakerFilterAtom)
  const [tradingMode] = useAtom(basicModeAtom)
  const openBasicDeposit = useAction(openBasicDepositDialogAction)

  const pair = pairState.value

  const handleSettingsOpen = () => openTradeSettings()

  return (
    <Root className={className}>
      <Body ref={bodyRef} variant="vertical">
        <Content>
          {pair && PREMIUM_ENABLED ? (
            <PremiumCurrencyBanner pair={pair} />
          ) : undefined}
          <TradingLiquidityAlert pair={pair} />
          {/*
            One panel or the other, never both. The two spend different
            money — a balance we custody against wallets whose keys are the
            person's — and showing them together is how somebody buys out of
            the one they did not mean to.
          */}
          {tradingMode === 'basic' ? (
            <BasicPanel
              token={
                pair
                  ? {
                      network: Network.toDto(pair.network),
                      address: pair.quote.address,
                      symbol: pair.quote.ticker,
                    }
                  : undefined
              }
              onDeposit={openBasicDeposit}
            />
          ) : (
            <TradingControls
              buyControlsModel={buyControlsModel}
              sellControlsModel={sellControlsModel}
              onSettingsOpen={handleSettingsOpen}
            />
          )}
          <TradingPairMetricsTable
            pair={pair}
            expandable={true}
            metricsTheme="dark"
          />
          <TauriEnvironment>
            <ActivePositionDetails />
          </TauriEnvironment>
          {pair ? (
            <>
              <PairStatsPanel pair={pair} />
              <TradingAuditList
                makerFilter={makerFilter}
                pair={pair}
                variant="short"
              />
            </>
          ) : (
            <TradingAuditListSkeleton />
          )}
        </Content>
      </Body>
      {pair ? (
        <TradeAuditDrawer
          open={auditOpen}
          onOpenChange={(open) => {
            if (!open) {
              hideAudit()
            }
          }}
          pair={pair}
        />
      ) : undefined}
    </Root>
  )
}

const Root = styled.aside`
  grid-column: 2;
  grid-row: 1 / -1;
  align-self: start;

  /* Below layout sticky topbar — avoids competing sticky at top: 0 (jumps while scrolling) */
  position: sticky;
  top: ${TOPBAR_HEIGHT}px;

  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 380px;

  border-inline-start: 1px solid var(--background-3);
  background-color: var(--appearance-1);
  overflow-x: hidden;
  overflow-y: hidden;

  ${layoutHeightCss}
`

const Content = styled.div`
  display: grid;
  position: relative;
  gap: 10px;
  padding: 10px;
  grid-template-rows: auto auto auto auto 1fr;
`

const Body = styled(FadeOverflow)`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 100%;

  ${hiddenScrollbarCss}
`
